<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { showToast } from 'vant'
import { useDebounceFn } from '@vueuse/core'
import FormField from '@/components/FormField.vue'
import EyesOpenIcon from '@/svg/eyes-open.svg'
import EyesCloseIcon from '@/svg/eyes-close.svg'
import UpgradeIcon from '@/svg/upgrade.svg'
import OkIcon from '@/svg/status/ok.svg'
import { memberAccountCheckApi, memberAccountUpgradeApi } from '@/api/member'
import { useAssetsStore } from '@/stores/assets'
import { getErrorMessage } from '@/composables/usePinVerifyMutation'
import { openAppOrDownloadPage, toastify } from '@/utils'
import type { SecurityPromptType } from '@/types/securityPrompt'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  completed: [type: SecurityPromptType]
}>()

const assetsStore = useAssetsStore()

const account = ref('')
const password = ref('')
const showPassword = ref(false)
const submitting = ref(false)
const accountChecking = ref(false)
const accountAvailable = ref<boolean | null>(null)
const credentialCardRef = ref<HTMLElement | null>(null)
const upgradeStep = ref<'form' | 'success'>('form')
const showSuccessDialog = ref(false)

const formVisible = computed({
  get: () => visible.value && upgradeStep.value === 'form',
  set: (value) => {
    if (!value) visible.value = false
  },
})

const visible = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value),
})

const isAccountFormatValid = (value: string) => {
  const val = value.trim()
  return /\d/.test(val) && /[a-z]/i.test(val) && val.length >= 6 && val.length <= 18
}

const isPasswordFormatValid = computed(() => {
  const pwd = password.value
  return pwd.length >= 8 && /\d/.test(pwd) && /[a-zA-Z]/.test(pwd)
})

const accountFormatError = computed(() => {
  if (!account.value) return ''
  return isAccountFormatValid(account.value) ? '' : '6-18位字母数字，不能有特殊符号，不能纯数字'
})
const passwordFormatError = computed(() => {
  if (!password.value) return ''
  const pwd = password.value
  return pwd.length >= 8 && /\d/.test(pwd) && /[a-zA-Z]/.test(pwd)
    ? ''
    : '新密码必须8位以上，至少含有1个数字+1个字母'
})

const accountAvailabilityError = computed(() => {
  if (!account.value || !isAccountFormatValid(account.value)) return ''
  if (accountChecking.value) return ''
  if (accountAvailable.value === false) return '用户名不可用'
  return ''
})

const canSubmit = computed(
  () =>
    isAccountFormatValid(account.value) &&
    accountAvailable.value === true &&
    isPasswordFormatValid.value &&
    !accountChecking.value &&
    !submitting.value,
)

const checkAccountAvailability = useDebounceFn(async (value: string) => {
  if (!isAccountFormatValid(value)) {
    accountAvailable.value = null
    return
  }

  accountChecking.value = true
  try {
    const res = await memberAccountCheckApi({ account: value.trim() })
    accountAvailable.value = res.data?.available ?? false
  } catch {
    accountAvailable.value = null
  } finally {
    accountChecking.value = false
  }
}, 400)

watch(visible, (value) => {
  if (!value) {
    account.value = ''
    password.value = ''
    showPassword.value = false
    accountAvailable.value = null
    accountChecking.value = false
    upgradeStep.value = 'form'
    showSuccessDialog.value = false
  }
})

const handleSaveImage = async () => {
  if (!account.value || !password.value) {
    showToast('请先填写账号和密码')
    return
  }
  if (!credentialCardRef.value) return

  try {
    const html2canvasModule = await import('html2canvas').catch(() => null)
    if (!html2canvasModule) {
      throw new Error('html2canvas not available')
    }

    const canvas = await html2canvasModule.default(credentialCardRef.value, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
    })

    const dataUrl = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = `幸运平台账号-${Date.now()}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    showToast('已保存图片')
  } catch (error) {
    console.error('保存图片失败:', error)
    showToast('保存图片失败，请重试')
  }
}

const handleSubmit = async () => {
  if (!canSubmit.value) return

  submitting.value = true
  try {
    await memberAccountUpgradeApi({
      account: account.value.trim(),
      password: password.value,
    })
    await assetsStore.refreshProfile()
    upgradeStep.value = 'success'
    showSuccessDialog.value = true
  } catch (error: unknown) {
    toastify({ type: 'danger', message: getErrorMessage(error, '账号升级失败') })
  } finally {
    submitting.value = false
  }
}

function handleOpenApp() {
  openAppOrDownloadPage()
}

watch(showSuccessDialog, (value) => {
  if (!value && upgradeStep.value === 'success') {
    visible.value = false
    emit('completed', 'account_upgrade')
  }
})
</script>

<template>
  <van-popup
    v-model:show="formVisible"
    position="bottom"
    round
    safe-area-inset-bottom
    closeable
    :close-on-click-overlay="false"
    class="account-upgrade-popup"
  >
    <div class="flex flex-col">
      <div class="relative flex items-center justify-center h-12 border-b border-border-light">
        <h3 class="text-base font-semibold text-text-primary m-0">升级账号</h3>
      </div>

      <div class="px-4 pt-4 pb-2">
        <div class="flex items-start gap-2 mb-4">
          <UpgradeIcon class="size-10 shrink-0" />
          <p class="text-sm text-text-primary leading-relaxed m-0">
            设置账号和登录密码即可升级，升级后即可与APP互通，请务必牢记账号和密码哦！
          </p>
        </div>

        <van-cell-group :border="false">
          <form-field
            v-model="account"
            name="account"
            label-width="0"
            placeholder="请填写您要设置的会员账号"
            maxlength="18"
            clearable
            @blur="checkAccountAvailability(account)"
          />
          <p v-if="accountFormatError" class="field-external-error" role="alert">
            {{ accountFormatError }}
          </p>
          <p v-else-if="accountAvailabilityError" class="field-external-error" role="alert">
            {{ accountAvailabilityError }}
          </p>

          <form-field
            v-model="password"
            name="password"
            class="mt-4"
            label-width="0"
            :type="showPassword ? 'text' : 'password'"
            placeholder="请填写您要设置的登录密码"
            maxlength="20"
            clearable
          >
            <template #button>
              <EyesOpenIcon
                v-if="showPassword"
                class="size-5 cursor-pointer shrink-0"
                @click.stop="showPassword = false"
              />
              <EyesCloseIcon
                v-else
                class="size-5 cursor-pointer shrink-0"
                @click.stop="showPassword = true"
              />
            </template>
          </form-field>
          <p v-if="passwordFormatError" class="field-external-error" role="alert">
            {{ passwordFormatError }}
          </p>
        </van-cell-group>

        <div class="flex gap-3 mt-6">
          <!-- <van-button plain class="flex-1 h-11" @click="handleSaveImage">保存图片</van-button> -->
          <van-button
            type="primary"
            class="flex-1 h-11"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            下一步
          </van-button>
        </div>
      </div>
    </div>

    <div ref="credentialCardRef" class="credential-card" aria-hidden="true">
      <div class="credential-card__title">幸运平台 · 账号信息</div>
      <div class="credential-card__row">
        <span>会员账号</span>
        <strong>{{ account || '—' }}</strong>
      </div>
      <div class="credential-card__row">
        <span>登录密码</span>
        <strong>{{ password || '—' }}</strong>
      </div>
      <p class="credential-card__tip">请妥善保管，遗忘后无法找回</p>
    </div>
  </van-popup>

  <van-popup
    v-model:show="showSuccessDialog"
    position="bottom"
    round
    closeable
    :close-on-click-overlay="false"
    class="upgrade-success-popup"
  >
    <div class="upgrade-success">
      <h3 class="upgrade-success__title">升级成功</h3>
      <OkIcon class="size-16 text-auxiliary-green" />
      <p class="upgrade-success__message"><b>恭喜您成功升级！</b>是否立即下载和登录APP体验？</p>
      <div class="upgrade-success__actions">
        <van-button plain class="flex-1 h-11" @click="handleOpenApp">下载APP</van-button>
        <van-button type="primary" class="flex-1 h-11" @click="handleOpenApp">登录APP</van-button>
      </div>
    </div>
  </van-popup>
</template>

<style scoped>
.field-external-error {
  margin: 6px 0 0;
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--van-danger-color);
}

.credential-card {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 320px;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-sizing: border-box;
}

.credential-card__title {
  font-size: 18px;
  font-weight: 700;
  color: #111;
  margin-bottom: 20px;
  text-align: center;
}

.credential-card__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
}

.credential-card__row strong {
  color: #111;
  font-weight: 600;
  word-break: break-all;
  text-align: right;
}

.credential-card__tip {
  margin: 16px 0 0;
  font-size: 12px;
  color: #e6a23c;
  text-align: center;
}

.upgrade-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 20px 20px;
  text-align: center;
}

.upgrade-success__title {
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 600;
  color: var(--van-text-color);
}

.upgrade-success__message {
  margin: 16px 0 24px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--van-text-color);
}

.upgrade-success__actions {
  display: flex;
  gap: 12px;
  width: 100%;
}
</style>
